import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPricingPlansSettingsV1PricingPlansSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/pricing-plans/settings/v1',
        destPath: '/v1',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/pricing-plans-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_pricing-plans_pricing-plans-settings';

/** Retrieves a site's Pricing Plans Settings. */
export function getPricingPlansSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getPricingPlansSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.pricing_plans.settings.v1.pricing_plans_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.pricing_plans.settings.v1.PricingPlansSettingsService.GetPricingPlansSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPricingPlansSettingsV1PricingPlansSettingsServiceUrl({
        protoPath: '/v1/pricing-plans-settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pricingPlansSettings.createdDate' },
              { path: 'pricingPlansSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPricingPlansSettings;
}

/**
 * Updates a site's Pricing Plans settings.
 *
 * Each time the Pricing Plans settings are updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the Pricing Plans settings.
 * This ensures you're working with the latest Pricing Plans settings and prevents unintended overwrites.
 */
export function updatePricingPlansSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePricingPlansSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'pricingPlansSettings.createdDate' },
          { path: 'pricingPlansSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.pricing_plans.settings.v1.pricing_plans_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.pricing_plans.settings.v1.PricingPlansSettingsService.UpdatePricingPlansSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPricingPlansSettingsV1PricingPlansSettingsServiceUrl({
        protoPath: '/v1/pricing-plans-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'pricingPlansSettings.createdDate' },
              { path: 'pricingPlansSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePricingPlansSettings;
}
